import mixpanel from 'mixpanel-browser'
import type { UserService } from '@duck/api-definitions'
import { isClientDevEnv } from '@/client-env'

export type ClientAnalyticsEvent =
  | 'loginCTA'

let initialized = false

export const ClientAnalytics = {
  get isInitialized() {
    return initialized
  },

  init() {
    const token = import.meta.env.VITE_MIXPANEL_TOKEN

    if (!token && import.meta.env.MODE !== 'development') {
      throw new Error('VITE_MIXPANEL_TOKEN is required for non-development builds')
    }

    if (token && !isClientDevEnv) {
      mixpanel.init(token, {
        debug: false,
        track_pageview: false,
        persistence: 'localStorage',
        record_sessions_percent: 30,
        record_heatmap_data: true,
        record_mask_all_text: false,
        record_mask_text_selector: ['#billing-section'],
        api_host: '/_mp',
        cdn: '/_mp',
      })
      initialized = true
    }
  },

  trackPageview() {
    if (!initialized) return
    mixpanel.track_pageview({ page: window.location.pathname })
  },

  identify(user: UserService.User) {
    if (!initialized) return
    try {
      mixpanel.identify(user.id)
      mixpanel.people.set({
        $email: user.email,
        $first_name: user.firstName,
        $last_name: user.lastName,
        role: user.role,
        source: user.source,
        organizationId: user.organizationId || 'no-org',
      })
      mixpanel.set_group('organizationId', user.organizationId || 'no-org')

      // Record all sessions for non-xp-lab users only
      if (!user.email?.endsWith('@xp-lab.com')) {
        mixpanel.start_session_recording()
      }
    } catch {
      // Silently ignore analytics failures
    }
  },

  reset() {
    if (!initialized) return
    try {
      mixpanel.stop_session_recording()
      mixpanel.reset()
    } catch {
      // Silently ignore analytics failures
    }
  },

  track(event: ClientAnalyticsEvent, properties?: Record<string, unknown>) {
    if (!initialized) return
    try {
      mixpanel.track(event, properties)
    } catch {
      // Silently ignore analytics failures
    }
  },
}
